import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import { api } from "../lib/api";

/**
 * Banner di stato servizio (letto da /api/system/status).
 *
 * Policy attuale (SI-008, no rumore per l'utente):
 *   • unhealthy               → banner rosso critico (app non usabile)
 *   • endpoint irraggiungibile per > 30s consecutivi → banner rosso
 *   • degraded                → **NESSUN banner** (rumore inutile, non actionable)
 *   • healthy                 → nessun banner
 *
 * Retry: 15s quando non ok, 5 min quando ok.
 * La diagnostica per l'owner è disponibile in /settings/system, non qui.
 */
const UNREACHABLE_GRACE_MS = 30_000;

export default function SystemStatusBanner() {
  const [state, setState] = useState("unknown"); // healthy | unhealthy | unreachable | unknown

  useEffect(() => {
    let alive = true;
    let timer = null;
    let firstFailureAt = null; // ms — inizio di uno streak di fetch falliti

    const classifyServer = (s) => {
      if (!s) return "unhealthy";
      if (s.status === "healthy" || s.status === "unhealthy") return s.status;
      // Legacy fallback: server prima dell'aggregato → mappa manualmente
      if (s.status === "degraded") return "healthy"; // degraded viene silenziato
      if (s.database !== "ok") return "unhealthy";
      return "healthy";
    };

    const check = async () => {
      try {
        const s = await api.systemStatus();
        if (!alive) return;
        firstFailureAt = null;
        const st = classifyServer(s);
        setState(st);
        timer = setTimeout(check, st === "healthy" ? 300_000 : 15_000);
      } catch {
        if (!alive) return;
        const now = Date.now();
        if (firstFailureAt == null) firstFailureAt = now;
        // Solo dopo 30s continui di fetch fallito mostriamo il rosso:
        // un blip di rete non deve spaventare l'utente.
        const persistent = now - firstFailureAt >= UNREACHABLE_GRACE_MS;
        setState(persistent ? "unreachable" : "healthy");
        timer = setTimeout(check, 15_000);
      }
    };

    check();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, []);

  // Mai visibile per healthy/unknown/degraded — solo per errori critici.
  if (state !== "unhealthy" && state !== "unreachable") return null;

  return (
    <div
      data-testid="system-status-banner"
      data-state={state}
      role="alert"
      className="fixed top-0 inset-x-0 z-[100] shadow-lg"
      style={{ background: "#B42318", color: "white" }}
    >
      <div className="max-w-6xl mx-auto flex items-center gap-3 px-4 py-2.5 text-sm">
        <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span className="flex-1">
          Servizio non disponibile — riprova tra qualche minuto.
        </span>
      </div>
    </div>
  );
}
